import React, { useRef, useState } from 'react';
import { ChevronLeft, Check, Ban, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import {
  ADD_PHOTOS_BUTTON,
  AUTHORING_ADVICE,
  ACCEPTED_EXAMPLE_TYPES,
  CAPTION_LABEL,
  CAPTION_PLACEHOLDER,
  DROP_ZONE_LIMITS,
  DROP_ZONE_TITLE,
  EDITOR_FOOTER_NOTE,
  EDITOR_TITLE,
  MOVE_DOWN,
  MOVE_UP,
  REMOVE,
  REMOVE_HINT,
  VERDICT_BAD,
  VERDICT_GOOD,
  backLabel,
  editorSubtitle,
} from '@/lib/exampleAuthoringCopy';
import {
  addExample,
  move,
  remove,
  select,
  setCaption,
  setVerdict,
  type ExampleDraft,
} from '@/lib/exampleDraft';
import { checklistExampleService } from '@/services/checklistExampleService';
import { NOT_DRAGGING, carriesFiles, onDropOrCancel, onEnter, onLeave } from '@/lib/fileDropTarget';

interface ChecklistItemExamplesEditorProps {
  itemName: string;
  tenantId: string;
  draft: ExampleDraft;
  onDraftChange: (next: ExampleDraft) => void;
  onBack: () => void;
}

/**
 * EXP-2.2 / EXP-2.3 / EXP-2.4 · The examples editor. The second screen.
 *
 * ⚠️ NO CANCEL AND NO SAVE LIVE HERE, and their absence is the design rather
 * than an omission. Cancel and Save belong to the item; a second Save on this
 * screen would imply examples commit on their own, which they do not. The
 * footer says so in words instead.
 *
 * ⚠️ Back does not save and does not discard. It slides the panel right and
 * leaves the draft exactly as it is, which is why the draft lives on the
 * item's state and not on this component's.
 */
export function ChecklistItemExamplesEditor({
  itemName,
  tenantId,
  draft,
  onDraftChange,
  onBack,
}: ChecklistItemExamplesEditorProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const captionRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  /**
   * The counter and the file-drag predicate live in `fileDropTarget.ts` with
   * their own tests. Both are the parts that actually break: a boolean
   * flickers as the pointer crosses children, and a text drag would light a
   * target that then refuses the drop.
   */
  const [drag, setDrag] = useState(NOT_DRAGGING);
  const isDragging = drag.active;

  const dropHandlers = {
    onDragEnter: (e: React.DragEvent) => {
      if (!carriesFiles(e.dataTransfer)) return;
      e.preventDefault();
      setDrag(onEnter);
    },
    onDragOver: (e: React.DragEvent) => {
      if (!carriesFiles(e.dataTransfer)) return;
      // ⚠️ Without preventDefault the browser refuses the drop and navigates
      // to the image instead, losing the whole form.
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!carriesFiles(e.dataTransfer)) return;
      setDrag(onLeave);
    },
    onDrop: (e: React.DragEvent) => {
      if (!carriesFiles(e.dataTransfer)) return;
      e.preventDefault();
      setDrag(onDropOrCancel());
      // ⚠️ The whole FileList, so dropping six at once adds six.
      handleFiles(e.dataTransfer.files);
    },
  };

  const selected = draft.selected;
  const entry = selected !== null ? draft.entries[selected] : undefined;

  const handleFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;

    setIsUploading(true);
    let next = draft;

    for (const file of list) {
      try {
        const uploaded = await checklistExampleService.uploadPending({
          file,
          tenantId,
          draftId: draft.draftId,
        });
        if (uploaded) {
          next = addExample(next, {
            key: uploaded.storagePath,
            storagePath: uploaded.storagePath,
            url: uploaded.url,
          });
        }
      } catch (error) {
        // ⚠️ One bad file must not lose the good ones. The loop continues and
        // the draft keeps every upload that worked.
        toast({
          title: 'Could not add that photo',
          description: error instanceof Error ? error.message : 'Upload failed',
          variant: 'destructive',
        });
      }
    }

    onDraftChange(next);
    setIsUploading(false);

    // EXP-2.3 · each new example lands selected with the caption focused.
    window.setTimeout(() => captionRef.current?.focus(), 0);
  };

  return (
    // ⚠️ THE WHOLE SCREEN IS THE DROP TARGET, not just the empty state's zone.
    // With photos already added the empty zone is gone, and before this the
    // only way to add another was to click and browse — a dead end an admin
    // hits the moment they have one photo and want five.
    <div className="relative flex h-full flex-col" {...dropHandlers}>
      {/* Header. The back control names the item it returns to. */}
      <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 rounded px-0.5 text-xs font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <ChevronLeft className="h-3 w-3" />
            {backLabel(itemName)}
          </button>
          <div className="mt-0.5 text-sm font-semibold text-foreground">{EDITOR_TITLE}</div>
          <div className="text-xs text-muted-foreground">{editorSubtitle(draft.entries.length)}</div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_EXAMPLE_TYPES.join(',')}
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files) handleFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {/* EXP-2.4 · with none authored, open on the drop zone, not an empty rail. */}
      {draft.entries.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            /* ⚠️ No drag handlers of its own. The root owns them, and a second
               set here would double-count enters and leaves against the shared
               depth counter, leaving the highlight stuck on after a drop. */
            className={[
              'w-full rounded-lg border-2 border-dashed p-6 text-xs transition-colors',
              isDragging
                ? 'border-primary bg-primaryLight text-primary'
                : 'border-border text-muted-foreground hover:border-primary hover:text-primary',
            ].join(' ')}
          >
            {isUploading ? (
              <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary" />
            ) : (
              <>
                <span className="mb-1 block text-sm font-semibold text-foreground">
                  {DROP_ZONE_TITLE}
                </span>
                {DROP_ZONE_LIMITS}
              </>
            )}
          </button>

          {/* ⚠️ The advice. The only copy in this feature that shapes what gets
              authored, and asserted on in exampleAuthoringCopy.test.ts. */}
          <p className="max-w-[34ch] text-xs leading-relaxed text-muted-foreground">
            {AUTHORING_ADVICE}
          </p>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1">
          {/* The rail: order number and verdict dot on every thumbnail. */}
          <div className="flex w-24 flex-none flex-col gap-2 overflow-y-auto border-r border-border bg-muted/30 p-2">
            {draft.entries.map((item, n) => (
              <button
                key={item.key}
                type="button"
                onClick={() => onDraftChange(select(draft, n))}
                aria-current={n === selected}
                aria-label={`Example ${n + 1}`}
                className={[
                  'relative block overflow-hidden rounded border-2 transition-colors',
                  n === selected ? 'border-primary' : 'border-transparent hover:border-border',
                ].join(' ')}
              >
                <img src={item.url} alt="" className="block aspect-[4/3] w-full object-cover" />
                <span className="absolute left-1 top-1 rounded bg-slate-900/70 px-1 text-[9px] font-bold text-white">
                  {n + 1}
                </span>
                <span
                  className={[
                    'absolute bottom-1 right-1 h-2.5 w-2.5 rounded-full border-[1.5px] border-white',
                    item.verdict === 'good' ? 'bg-emerald-500' : 'bg-red-500',
                  ].join(' ')}
                />
              </button>
            ))}

            {/* Click to browse, or drop straight onto it. The drop itself is
                caught by the root, so this only has to LOOK like a target. */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className={[
                'rounded border-[1.5px] border-dashed py-2 text-[10.5px] font-semibold transition-colors disabled:opacity-50',
                isDragging
                  ? 'border-primary bg-primaryLight text-primaryHover'
                  : 'border-border text-muted-foreground hover:border-primary hover:text-primary',
              ].join(' ')}
            >
              {isUploading ? (
                <Loader2 className="mx-auto h-3 w-3 animate-spin" />
              ) : isDragging ? (
                'Drop'
              ) : (
                '+ Add'
              )}
            </button>
          </div>

          {/* The editor pane for the selected example. */}
          {entry && selected !== null && (
            <div className="flex min-w-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
              <img
                src={entry.url}
                alt=""
                className="h-40 w-full flex-none rounded-md border border-border object-cover"
              />

              <div className="grid gap-1.5">
                <Label htmlFor="example_caption" className="text-xs font-semibold">
                  {CAPTION_LABEL}
                </Label>
                <Input
                  id="example_caption"
                  ref={captionRef}
                  value={entry.caption}
                  placeholder={CAPTION_PLACEHOLDER}
                  onChange={(e) => onDraftChange(setCaption(draft, selected, e.target.value))}
                />
              </div>

              <div className="inline-flex self-start overflow-hidden rounded-md border border-border">
                <button
                  type="button"
                  aria-pressed={entry.verdict === 'good'}
                  onClick={() => onDraftChange(setVerdict(draft, selected, 'good'))}
                  className={[
                    'inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold transition-colors',
                    entry.verdict === 'good'
                      ? 'bg-primaryLight text-primaryHover'
                      : 'bg-background text-muted-foreground hover:text-foreground',
                  ].join(' ')}
                >
                  <Check className="h-3 w-3" />
                  {VERDICT_GOOD}
                </button>
                <button
                  type="button"
                  aria-pressed={entry.verdict === 'bad'}
                  onClick={() => onDraftChange(setVerdict(draft, selected, 'bad'))}
                  className={[
                    'inline-flex items-center gap-1.5 border-l border-border px-2.5 py-1 text-[11px] font-semibold transition-colors',
                    entry.verdict === 'bad'
                      ? 'bg-red-50 text-red-700'
                      : 'bg-background text-muted-foreground hover:text-foreground',
                  ].join(' ')}
                >
                  <Ban className="h-3 w-3" />
                  {VERDICT_BAD}
                </button>
              </div>

              <div className="flex flex-wrap gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={selected === 0}
                  onClick={() => onDraftChange(move(draft, selected, -1))}
                >
                  <ChevronUp className="mr-1 h-3 w-3" />
                  {MOVE_UP}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={selected >= draft.entries.length - 1}
                  onClick={() => onDraftChange(move(draft, selected, 1))}
                >
                  <ChevronDown className="mr-1 h-3 w-3" />
                  {MOVE_DOWN}
                </Button>
                {/* EXP-2.3 · no confirmation dialog. The copy carries the
                    warning instead, because interrogating an admin about a
                    photo they just uploaded is worse than the undo cost. */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  title={REMOVE_HINT}
                  onClick={() => onDraftChange(remove(draft, selected))}
                  className="hover:border-destructive hover:text-destructive"
                >
                  {REMOVE}
                </Button>
              </div>

              <p className="text-[11px] text-muted-foreground">{REMOVE_HINT}</p>
            </div>
          )}
        </div>
      )}

      {/* The target, made visible. Without the empty state's dashed box there
          is otherwise nothing on screen saying a drop will be accepted. */}
      {isDragging && draft.entries.length > 0 && (
        <div
          aria-hidden="true"
          // ⚠️ NOT `bg-primaryLight/90`. Theme tokens hold hex values, so
          // Tailwind's opacity modifier compiles to invalid CSS and is dropped
          // silently — the overlay would be fully transparent and the drop
          // would look like it did nothing. Same trap as the lit switch row.
          style={{ backgroundColor: 'color-mix(in srgb, var(--primaryLight) 92%, transparent)' }}
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-primary"
        >
          <span className="text-sm font-semibold text-primaryHover">
            {DROP_ZONE_TITLE}
          </span>
        </div>
      )}

      {/* ⚠️ No Cancel, no Save. The note says where they are instead. */}
      <div className="flex flex-none items-center justify-between gap-2 border-t border-border bg-muted/30 px-4 py-2.5">
        <span className="text-xs text-muted-foreground">{EDITOR_FOOTER_NOTE}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          {ADD_PHOTOS_BUTTON}
        </Button>
      </div>
    </div>
  );
}
